import React, { useState, useRef, useEffect } from 'react';
import { useNotifications } from '../Context/NotificationContext';
import { getCreatorById } from '../services/simpleCreatorService';
import { getAuth } from 'firebase/auth';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { ProjectIdGenerator } from '../utils/projectIdGenerator';
import './NotificationBell.css';

function NotificationBell({ setActiveTab, setShowCreatorModal, setSelectedCreator }) {
  const [showDropdown, setShowDropdown] = useState(false);
  const { notifications, unreadCount, markAsRead, deleteNotification, deleteAllNotifications } = useNotifications();
  const notifRef = useRef();

  useEffect(() => {
    function handleClickOutside(e) {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOpen = () => {
    setShowDropdown(!showDropdown);
  };

  const handleNotificationClick = async (notification) => {
    console.log('Notification clicked:', JSON.stringify(notification, null, 2));
    console.log('Notification keys:', Object.keys(notification));
    
    if (!notification.read) {
      markAsRead(notification.id);
    }

    // Handle navigation based on notification type and status
    if (notification.action === 'chat_message' || notification.type === 'chat' || notification.type === 'message' || notification.title?.includes('message') || notification.message?.includes('message')) {
      // Use projectId to find the creator from the brief
      const projectId = notification.projectId || notification.data?.projectId;
      const normalizedProjectId = projectId ? ProjectIdGenerator.normalizeProjectId(projectId) : null;
      
      if (normalizedProjectId) {
        try {
          const auth = getAuth();
          const user = auth.currentUser;
          if (!user) return;
          
          // Search all campaigns and briefs to find the creator with this projectId
          const campaignsRef = collection(db, 'users', user.uid, 'campaigns');
          const campaignsSnapshot = await getDocs(campaignsRef);
          
          let foundCreator = null;
          let foundCampaignId = null;
          let foundBriefId = null;
          let foundCampaignName = null;
          let foundBriefName = null;
          
          // Search through all campaigns and briefs
          for (const campaignDoc of campaignsSnapshot.docs) {
            const campaignId = campaignDoc.id;
            const campaignData = campaignDoc.data();
            const briefsRef = collection(db, 'users', user.uid, 'campaigns', campaignId, 'briefs');
            const briefsSnapshot = await getDocs(briefsRef);
            
            for (const briefDoc of briefsSnapshot.docs) {
              const briefId = briefDoc.id;
              const briefData = briefDoc.data();
              const creators = briefData.creators || [];
              
              // Find creator with matching projectId
              for (const creator of creators) {
                const creatorProjectId = ProjectIdGenerator.normalizeProjectId(creator.projectId) || creator.projectId;
                if (creatorProjectId && ProjectIdGenerator.areEquivalent(creatorProjectId, normalizedProjectId)) {
                  foundCreator = creator;
                  foundCampaignId = campaignId;
                  foundBriefId = briefId;
                  foundCampaignName = campaignData.name || campaignId;
                  foundBriefName = briefData.title || briefData.name || briefId;
                  break;
                }
              }
              if (foundCreator) break;
            }
            if (foundCreator) break;
          }
          
          if (foundCreator) {
            // Fetch full creator profile
            const fullCreatorProfile = await getCreatorById(foundCreator.id);
            
            const creatorData = {
              id: foundCreator.id,
              creator: fullCreatorProfile?.handle || fullCreatorProfile?.name || foundCreator.name || foundCreator.handle,
              name: fullCreatorProfile?.name || foundCreator.name,
              handle: fullCreatorProfile?.handle || foundCreator.handle,
              username: fullCreatorProfile?.handle || foundCreator.handle,
              avatar: fullCreatorProfile?.profileImage || fullCreatorProfile?.image || foundCreator.avatar || '/creatorimages/creatorpic1v2-min.png',
              profileImage: fullCreatorProfile?.profileImage || fullCreatorProfile?.image || foundCreator.avatar || '/creatorimages/creatorpic1v2-min.png',
              status: foundCreator.status || 'confirmed',
              campaignId: foundCampaignId,
              campaignName: foundCampaignName,
              briefId: foundBriefId,
              briefName: foundBriefName,
              briefTitle: foundBriefName,
              projectId: normalizedProjectId,
              openChatPanel: true, // Open chat panel directly
              // Include other creator profile data
              age: fullCreatorProfile?.age,
              gender: fullCreatorProfile?.gender,
              location: fullCreatorProfile?.location,
              tier: fullCreatorProfile?.tier || foundCreator.tier,
              // ... other profile fields
            };
            
            setSelectedCreator(creatorData);
            setShowCreatorModal(true);
          } else {
            console.log('Creator not found for projectId:', normalizedProjectId);
          }
        } catch (error) {
          console.error('Error finding creator from projectId:', error);
        }
      } else {
        console.log('No projectId found in notification');
      }
    } else if (notification.action === 'submitted' || notification.status === 'submitted' || notification.type === 'revision_resubmitted') {
      // Navigate to hand-in page for submitted content or revision resubmissions
      setActiveTab('handin');
    } else if (notification.action === 'awaiting' || notification.status === 'awaiting') {
      // Open creator modal for awaiting product/service
      if (notification.creatorId && notification.creatorName) {
        // Create a mock creator object for the modal
        const creatorData = {
          id: notification.creatorId,
          creator: notification.creatorName,
          status: notification.status || 'awaiting',
          statusText: notification.statusText || 'Awaiting product/service',
          progress: 25, // Default progress for awaiting
          due: 'Pending product',
          isOverdue: false,
          campaignName: notification.campaignName,
          briefName: notification.briefName
        };
        setSelectedCreator(creatorData);
        setShowCreatorModal(true);
      }
    } else if (notification.type === 'delivery_address_submitted') {
      // Handle delivery address submission notification
      if (notification.details?.creatorId && notification.details?.creatorName) {
        const creatorData = {
          id: notification.details.creatorId,
          creator: notification.details.creatorName,
          name: notification.details.creatorName,
          status: 'confirmed',
          statusText: 'Delivery Address Provided',
          progress: 30,
          due: 'Product shipping',
          isOverdue: false,
          campaignId: notification.details.campaignId,
          campaignName: notification.details.campaignName,
          briefId: notification.details.briefId,
          briefName: notification.details.briefName,
          deliveryAddress: notification.details.deliveryAddress
        };
        setSelectedCreator(creatorData);
        setShowCreatorModal(true);
      }
    } else if (notification.type === 'creator_progress' || ['filming', 'editing', 'review'].includes(notification.status)) {
      // Open creator modal for progress updates or in-progress creators
      const creatorId = notification.details?.creatorId || notification.creatorId;
      const creatorName = notification.details?.creatorName || notification.creatorName;
      const progressType = notification.details?.progressType || notification.status;
      
      if (creatorId && creatorName) {
        // Create a creator object for the modal
        const creatorData = {
          id: creatorId,
          creator: creatorName,
          name: creatorName,
          status: progressType === 'filming' ? 'filming' : progressType === 'editing' ? 'editing' : progressType === 'submitted' ? 'submitted' : progressType,
          statusText: progressType === 'filming' ? 'Filming' : progressType === 'editing' ? 'Editing' : progressType === 'submitted' ? 'Submitted' : progressType === 'productReceived' ? 'Product Received' : 'In Progress',
          progress: progressType === 'filming' ? 50 : progressType === 'editing' ? 75 : progressType === 'submitted' ? 90 : 25,
          due: progressType === 'filming' ? '5 Days' : progressType === 'editing' ? '2 Days' : progressType === 'submitted' ? 'Reviewing' : 'TBD',
          isOverdue: false,
          campaignId: notification.details?.campaignId || notification.campaignId,
          campaignName: notification.details?.campaignName || notification.campaignName,
          briefId: notification.details?.briefId || notification.briefId,
          briefName: notification.details?.briefName || notification.briefName || notification.details?.briefTitle
        };
        setSelectedCreator(creatorData);
        setShowCreatorModal(true);
      }
    }

    setShowDropdown(false);
  };

  const handleDeleteNotification = async (e, notificationId) => {
    e.stopPropagation();
    e.preventDefault();
    await deleteNotification(notificationId);
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    
    const now = new Date();
    const time = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  return (
    <div className="notif-wrapper" ref={notifRef}>
      <button className="notif-btn" onClick={handleOpen}>
        <img src="/icons/bell.svg" alt="Notifications" />
        {unreadCount > 0 && <span className="notif-pulse-dot"></span>}
      </button>
      {showDropdown && (
        <div className="notif-dropdown">
          <div className="notif-header">
            <p><strong>Notifications</strong></p>
            <div className="notif-header-buttons">
              {notifications.length > 0 && (
                <button className="clear-all-btn" onClick={deleteAllNotifications}>
                  Clear all
                </button>
              )}
            </div>
          </div>
          <div className="notif-list">
            {notifications.length > 0 ? (
              notifications
                .sort((a, b) => {
                  // Unread first, then by time
                  if (a.read !== b.read) return a.read ? 1 : -1;
                  const timeA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
                  const timeB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
                  return timeB - timeA;
                })
                .slice(0, 10)
                .map((notification) => (
                <div 
                  key={notification.id} 
                  className={`notif-item ${!notification.read ? 'unread' : ''} ${(notification.type === 'chat' || notification.type === 'message') ? 'chat-notification' : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notif-content">
                    <div className="notif-title">{notification.title}</div>
                    <div className="notif-message">{notification.message}</div>
                    <div className="notif-time">{formatTimeAgo(notification.createdAt)}</div>
                  </div>
                  <div className="notif-actions">
                    {!notification.read && <div className="unread-indicator"></div>}
                    <button 
                      className="delete-notif-btn"
                      onClick={(e) => handleDeleteNotification(e, notification.id)}
                      title="Delete notification"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-notifications">
                <p>No notifications yet</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
